import React from "react";
import { Chart, ChartOptions, ChartDataSets } from "chart.js";
import autobind from "class-autobind";
import { IGroupedTrend, ITrendSettingsDocument } from "../../../models/data/dashboard/Trends/TrendsData";
import * as utils from "../../../helpers/utils";

export interface IProps {
    groupedTrends: IGroupedTrend[];
    settings: ITrendSettingsDocument;
}

// Extend the ChartDataSets interface to include numerators and denominators and isSummation
interface CustomChartDataSets extends ChartDataSets {
    numerators: number[];
    denominators: number[];
    isSummation: boolean;
}

export default class TrendDayGraph extends React.Component<IProps, any> {
    trend_day_graph: Chart;
    colors = ["green", "blue", "orange", "purple", "red"]; // More colors if needed
    ctx: CanvasRenderingContext2D | null = null;

    constructor(props: IProps) {
        super(props);
        autobind(this);
    }

    componentDidMount() {
        const ctx: HTMLCanvasElement = document.getElementById("trend_day_graph") as HTMLCanvasElement;
        this.ctx = ctx.getContext("2d");
        this.loadChart();
    }

    componentDidUpdate() {
        this.loadChart();
    }

    loadChart() {
        if (this.trend_day_graph) {
            this.trend_day_graph.destroy();
        }
    
        // Create a set of unique hierarchy names
        const hierarchyNames = Array.from(new Set(this.props.groupedTrends.map(trend => trend.HierarchyName)));
        
        const trendData = this.props.groupedTrends.reduce((acc, trend) => {
            const uniqueTrendIndex = this.props.groupedTrends.findIndex(t => t.Settings.Id === trend.Settings.Id);
            const colorIndex = uniqueTrendIndex % this.colors.length;
            
            if (!acc[trend.Settings.Id]) {
                let seriesData = { Numerator: 0, Denominator: 0 };
                if (trend.SeriesData && trend.SeriesData.length > 0) {
                    seriesData = trend.SeriesData[0];
                }
                acc[trend.Settings.Id] = {
                    label: trend.Settings.Name,
                    isSummation: trend.Settings.AggregationSettings.AggregationType === 0 ,
                    backgroundColor: utils.getTrendColorGradient(this.colors[colorIndex], 1, this.ctx),
                    borderColor: utils.getTrendColor(this.colors[colorIndex] || "green"),
                    borderWidth: 1,
                    hoverBackgroundColor: utils.getTrendColorGradient(this.colors[colorIndex], 1, this.ctx),
                    hoverBorderColor: utils.getTrendColor(this.colors[colorIndex] || "green"),
                    data: new Array(hierarchyNames.length).fill(0), // Initialize with zeros
                    numerators: new Array(hierarchyNames.length).fill(0),
                    denominators: new Array(hierarchyNames.length).fill(0)
                } as CustomChartDataSets;
            }
    
            const hierarchyIndex = hierarchyNames.indexOf(trend.HierarchyName);
            acc[trend.Settings.Id].data[hierarchyIndex] = trend.Totals.PeriodTotal;
            if (trend.SeriesData && trend.SeriesData.length > 0) {
                acc[trend.Settings.Id].numerators[hierarchyIndex] = trend.SeriesData[0].Numerator || 0;
                acc[trend.Settings.Id].denominators[hierarchyIndex] = trend.SeriesData[0].Denominator || 0;
            }
    
            return acc;
        }, {} as { [key: string]: CustomChartDataSets });
    
        const chartData = {
            labels: hierarchyNames,
            datasets: Object.values(trendData)
        };
    
        const chartOptions: ChartOptions = {
            responsive: true,
            scales: {
                yAxes: [{
                    ticks: {
                        beginAtZero: true,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.props.settings.UnitName,
                    }
                }],
                xAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: 'Hierarchy Name',
                    }
                }]
            },
            legend: {
                display: true,
                position: "top"
            },
            tooltips: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem, data) => {
                        const dataset = data.datasets[tooltipItem.datasetIndex] as CustomChartDataSets;
                        const index = tooltipItem.index;
                        const numerator = dataset.numerators[index];
                        const denominator = dataset.denominators[index];
                        const isSummation = dataset.isSummation;
            
                        if (isSummation) {
                            // Handle the case for summation
                            return `${dataset.label}: ${tooltipItem.yLabel}`;
                        } else {
                            // Handle the case for non-summation
                            return `${dataset.label}: ${tooltipItem.yLabel}% (${numerator}/${denominator})`;
                        }
                    }
                }
            },
            maintainAspectRatio: true
        };
    
        this.trend_day_graph = new Chart(this.ctx, {
            type: 'bar',
            data: chartData,
            options: chartOptions
        });
    }

    render() {
        return (
            <div style={{ paddingTop: 5, paddingBottom: 5 }}>
                <canvas id="trend_day_graph" />
            </div>
        );
    }
}